import { useMemo, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { useScheduling, useUpdateScheduling } from '@/hooks/useSettings'
import { toast } from '@/components/ui/toast'
import { getApiErrorMessage } from '@/services/api'
import type { SchedulingSettings } from '@/types'

const DAY_ORDER = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const

const SHORT: Record<string, string> = {
  monday: 'mon',
  tuesday: 'tue',
  wednesday: 'wed',
  thursday: 'thu',
  friday: 'fri',
  saturday: 'sat',
  sunday: 'sun',
}

const sectionCardClass =
  'overflow-hidden rounded-2xl border border-[#e7e9ef] bg-white shadow-[0_8px_16px_-4px_rgba(38,45,66,0.1),0_1px_4px_-4px_rgba(38,45,66,0.05)]'

const fieldInputClass =
  'h-10 rounded-xl bg-[#f4f5f8] px-4 py-3 text-sm font-normal leading-[1.4] text-[#171a20] shadow-none placeholder:font-normal placeholder:text-[#6c7285]'

const saveButtonClass =
  'h-10 rounded-xl border border-[#ff5a1f] bg-[#ff5a1f] px-4 text-sm font-medium text-white shadow-none hover:bg-[#e6511c]'

const dayRowClass =
  'flex flex-col gap-3 rounded-2xl border border-[#e7e9ef] bg-white px-4 py-3 lg:flex-row lg:items-center'

const switchClass =
  'h-6 w-10 border-0 data-[state=checked]:bg-[#10b981] data-[state=unchecked]:bg-[#8f95a6] [&>span]:h-5 [&>span]:w-5 [&>span]:data-[state=checked]:translate-x-4 [&>span]:data-[state=unchecked]:translate-x-0.5'

interface DaySlot {
  enabled: boolean
  start: string
  end: string
}

interface State {
  default_duration: string
  interval: string
  slots: Record<string, DaySlot>
  respect_working_hours: boolean
  prevent_overlaps: boolean
  limit_per_day: boolean
  max_jobs_per_day: string
  travel_time: string
  reminder_before: string
  notify_on_modification: boolean
  task_reminder_time: string
}

function parseTimeToMinutes(value: string) {
  const [hours, minutes] = value.split(':').map(Number)

  if (
    !Number.isFinite(hours) ||
    !Number.isFinite(minutes) ||
    hours < 0 ||
    hours > 23 ||
    minutes < 0 ||
    minutes > 59
  ) {
    return null
  }

  return hours * 60 + minutes
}

function defaultSlots(): Record<string, DaySlot> {
  const slots: Record<string, DaySlot> = {}
  for (const day of DAY_ORDER) {
    slots[day] = {
      enabled: day !== 'saturday' && day !== 'sunday',
      start: '08:00',
      end: '18:00',
    }
  }
  return slots
}

const DEFAULT_STATE: State = {
  default_duration: '60',
  interval: '15',
  slots: defaultSlots(),
  respect_working_hours: true,
  prevent_overlaps: true,
  limit_per_day: false,
  max_jobs_per_day: '',
  travel_time: '0',
  reminder_before: '',
  notify_on_modification: true,
  task_reminder_time: '',
}

function fromApi(s: SchedulingSettings): State {
  const num = (v: number | null | undefined) =>
    v === null || v === undefined ? '' : String(v)
  const slots: Record<string, DaySlot> = {}
  for (const day of DAY_ORDER) {
    const slot = s.time_slots?.[day]
    slots[day] = {
      enabled: slot?.enabled ?? false,
      start: slot?.start ?? '08:00',
      end: slot?.end ?? '18:00',
    }
  }
  return {
    default_duration: String(s.intervention_duration_minutes ?? 60),
    interval: String(s.interval_minutes ?? 15),
    slots,
    respect_working_hours: s.rules?.respect_working_hours ?? true,
    prevent_overlaps: s.rules?.prevent_overlaps ?? true,
    limit_per_day: s.rules?.limit_interventions_per_day ?? false,
    max_jobs_per_day: num(s.rules?.max_jobs_per_day),
    travel_time: String(s.rules?.default_travel_time_minutes ?? 0),
    reminder_before: num(s.reminders?.reminder_before_intervention_minutes),
    notify_on_modification: s.reminders?.notify_on_modification ?? true,
    task_reminder_time: s.reminders?.default_task_reminder_time ?? '',
  }
}

function toApi(s: State): SchedulingSettings {
  const int = (v: string) => (v.trim() === '' ? null : Number(v))
  const time_slots: SchedulingSettings['time_slots'] = {}
  for (const day of DAY_ORDER) {
    time_slots[day] = {
      enabled: s.slots[day].enabled,
      start: s.slots[day].start,
      end: s.slots[day].end,
    }
  }
  return {
    time_slots,
    intervention_duration_minutes: Number(s.default_duration) || 0,
    interval_minutes: Number(s.interval) || 0,
    rules: {
      respect_working_hours: s.respect_working_hours,
      prevent_overlaps: s.prevent_overlaps,
      limit_interventions_per_day: s.limit_per_day,
      max_jobs_per_day: s.limit_per_day ? int(s.max_jobs_per_day) : null,
      default_travel_time_minutes:
        s.travel_time.trim() === '' ? 0 : Number(s.travel_time),
    },
    reminders: {
      reminder_before_intervention_minutes: int(s.reminder_before),
      notify_on_modification: s.notify_on_modification,
      default_task_reminder_time:
        s.task_reminder_time.trim() === '' ? null : s.task_reminder_time,
    },
  }
}

function FieldLabel({
  htmlFor,
  required,
  children,
}: {
  htmlFor?: string
  required?: boolean
  children: ReactNode
}) {
  return (
    <Label
      htmlFor={htmlFor}
      className='flex items-center gap-1 text-xs font-medium leading-4 text-[#171a20]'
    >
      {children}
      {required && <span className='text-[#d63b33]'> *</span>}
    </Label>
  )
}

function SectionHeading({ children }: { children: ReactNode }) {
  return (
    <div className='flex min-w-0 items-center border-l-4 border-[#ff5a1f] px-6 py-1'>
      <h2 className='font-heading text-sm font-semibold leading-[1.2] text-[#171a20]'>
        {children}
      </h2>
    </div>
  )
}

function SectionCard({
  title,
  children,
}: {
  title: string
  children: ReactNode
}) {
  return (
    <section className={sectionCardClass}>
      <header className='flex items-center gap-2 px-0 pb-2 pt-6 pr-6'>
        <div className='min-w-0 flex-1'>
          <SectionHeading>{title}</SectionHeading>
        </div>
      </header>
      <div className='flex flex-col gap-6 px-6 pb-6 pt-4'>{children}</div>
    </section>
  )
}

function TimeInput({
  value,
  onChange,
  disabled,
  error,
}: {
  value: string
  onChange: (value: string) => void
  disabled?: boolean
  error?: boolean
}) {
  return (
    <Input
      type='time'
      value={value}
      disabled={disabled}
      onChange={e => onChange(e.target.value)}
      className={cn(
        fieldInputClass,
        disabled && 'bg-[#e7e9ef] text-[#6c7285] font-normal',
        error && 'border border-destructive bg-white'
      )}
    />
  )
}

// 2.11.9 + 2.11.11 merged — Working days and hours configuration. Per-day
// open/close times, general timing and reminders. Timezone fixed to
// Europe/Paris (single region). Admin only.
export function WorkingDaysHoursPanel() {
  const { t, i18n } = useTranslation('settings')
  const { data, isLoading, isError } = useScheduling()
  const update = useUpdateScheduling()
  const [draft, setDraft] = useState<State | null>(null)
  const [errors, setErrors] = useState<Record<string, string>>({})

  const baseState = useMemo(
    () => (data?.data ? fromApi(data.data) : DEFAULT_STATE),
    [data]
  )
  const state = draft ?? baseState

  const isSaving = update.isPending

  const set = (patch: Partial<State>) =>
    setDraft(s => ({ ...(s ?? baseState), ...patch }))
  const setDay = (day: string, patch: Partial<DaySlot>) =>
    setDraft(s => {
      const next = s ?? baseState
      return {
        ...next,
        slots: { ...next.slots, [day]: { ...next.slots[day], ...patch } },
      }
    })

  function formatWorkingHours(day: string) {
    const slot = state.slots[day]

    if (!slot.enabled) {
      return t('workingDays.badges.rest')
    }

    const start = parseTimeToMinutes(slot.start)
    const end = parseTimeToMinutes(slot.end)

    if (start === null || end === null || end <= start) {
      return t('workingDays.badges.hourPlural', { value: '0' })
    }

    const hours = (end - start) / 60
    const formattedHours = new Intl.NumberFormat(i18n.language, {
      maximumFractionDigits: Number.isInteger(hours) ? 0 : 1,
      minimumFractionDigits: Number.isInteger(hours) ? 0 : 1,
    }).format(hours)

    return hours === 1
      ? t('workingDays.badges.hourSingle', { value: formattedHours })
      : t('workingDays.badges.hourPlural', { value: formattedHours })
  }

  function validate(): boolean {
    const next: Record<string, string> = {}
    if (!(Number(state.default_duration) > 0))
      next.default_duration = t('workingDays.validation.durationPositive')

    const anyEnabled = DAY_ORDER.some(d => state.slots[d].enabled)
    if (!anyEnabled) next.days = t('workingDays.validation.dayRequired')

    for (const day of DAY_ORDER) {
      const slot = state.slots[day]
      if (slot.enabled && slot.end <= slot.start) {
        next[`slot_${day}`] = t('workingDays.validation.endAfterStart')
      }
    }

    if (state.limit_per_day && !(Number(state.max_jobs_per_day) > 0)) {
      next.max_jobs_per_day = t('workingDays.validation.maxJobsRequired')
    }

    setErrors(next)
    return Object.keys(next).length === 0
  }

  function save() {
    if (!validate()) return
    update.mutate(toApi(state), {
      onSuccess: () => {
        setDraft(null)
        toast({ variant: 'success', title: t('workingDays.toast.saved') })
      },
      onError: e =>
        toast({ variant: 'destructive', title: getApiErrorMessage(e) }),
    })
  }

  if (isLoading) {
    return (
      <div className='space-y-5'>
        <Skeleton className='h-[1040px] rounded-2xl' />
        <Skeleton className='h-[300px] rounded-2xl' />
      </div>
    )
  }

  if (isError) {
    return (
      <section className={sectionCardClass}>
        <div className='px-6 py-8 text-center text-sm text-destructive'>
          {t('workingDays.loadError')}
        </div>
      </section>
    )
  }

  return (
    <div className='flex min-h-0 flex-1 flex-col'>
      <div className='space-y-5 pb-24'>
        <SectionCard title={t('workingDays.title')}>
          <div className='grid grid-cols-1 gap-4 border-b border-dashed border-[#e7e9ef] pb-6 lg:grid-cols-2'>
            <div className='flex flex-col gap-1'>
              <FieldLabel htmlFor='wd-dur' required>
                {t('workingDays.fields.defaultDuration')}
              </FieldLabel>
              <Input
                id='wd-dur'
                type='number'
                value={state.default_duration}
                onChange={e => {
                  set({ default_duration: e.target.value })
                  setErrors(s => ({ ...s, default_duration: '' }))
                }}
                className={cn(
                  fieldInputClass,
                  errors.default_duration &&
                    'border border-destructive bg-white'
                )}
              />
              {errors.default_duration && (
                <p className='text-sm text-destructive'>
                  {errors.default_duration}
                </p>
              )}
            </div>

            <div className='flex flex-col gap-1'>
              <FieldLabel htmlFor='wd-int' required>
                {t('workingDays.fields.interval')}
              </FieldLabel>
              <Input
                id='wd-int'
                type='number'
                value={state.interval}
                onChange={e => set({ interval: e.target.value })}
                className={fieldInputClass}
              />
            </div>
          </div>

          <h3 className='font-heading text-sm font-semibold leading-[1.2] text-[#171a20]'>
            {t('workingDays.fields.workingHours')}
          </h3>

          <div className='flex flex-col gap-3'>
            {DAY_ORDER.map(day => {
              const slot = state.slots[day]
              const slotError = errors[`slot_${day}`]
              const workingHours = formatWorkingHours(day)

              return (
                <div key={day} className='flex flex-col gap-1'>
                  <div
                    className={cn(
                      dayRowClass,
                      !slot.enabled && 'bg-[#f4f5f8] text-[#6c7285]'
                    )}
                  >
                    <div className='flex min-w-[160px] shrink-0 items-center gap-3'>
                      <Switch
                        checked={slot.enabled}
                        onCheckedChange={v => setDay(day, { enabled: v })}
                        className={switchClass}
                        aria-label={t(`workingDays.days.${SHORT[day]}`)}
                      />
                      <span className='text-sm font-medium leading-[1.4] text-[#171a20]'>
                        {t(`workingDays.days.${SHORT[day]}`)}
                      </span>
                    </div>

                    <div className='flex flex-1 items-center gap-2'>
                      <div className='w-full max-w-[136px]'>
                        <TimeInput
                          value={slot.start}
                          disabled={!slot.enabled}
                          error={!!slotError}
                          onChange={value => setDay(day, { start: value })}
                        />
                      </div>
                      <span className='text-[#e7e9ef]'>-</span>
                      <div className='w-full max-w-[136px]'>
                        <TimeInput
                          value={slot.end}
                          disabled={!slot.enabled}
                          error={!!slotError}
                          onChange={value => setDay(day, { end: value })}
                        />
                      </div>
                    </div>

                    {workingHours && (
                      <div className='lg:ml-auto'>
                        <span
                          className={cn(
                            'inline-flex min-w-[82px] items-center justify-center rounded-[10px] px-3 py-1.5 text-sm font-medium leading-[1.4]',
                            slot.enabled
                              ? 'bg-[#d6f5e4] text-[#117553]'
                              : 'bg-[#e7e9ef] text-[#171a20]'
                          )}
                        >
                          {workingHours}
                        </span>
                      </div>
                    )}
                  </div>

                  {slotError && (
                    <p className='text-sm text-destructive'>{slotError}</p>
                  )}
                </div>
              )
            })}
            {errors.days && (
              <p className='text-sm text-destructive'>{errors.days}</p>
            )}
          </div>

          <div className='rounded-2xl bg-white'>
            <div className='border-b border-[#e7e9ef] py-3'>
              <div className='flex items-center gap-3'>
                <Label
                  htmlFor='wd-rwh'
                  className='flex-1 text-sm font-medium leading-[1.4] text-[#171a20]'
                >
                  {t('workingDays.rules.respectWorkingHours')}
                </Label>
                <Switch
                  id='wd-rwh'
                  checked={state.respect_working_hours}
                  onCheckedChange={value =>
                    set({ respect_working_hours: value })
                  }
                  className={switchClass}
                />
              </div>
            </div>

            <div className='border-b border-[#e7e9ef] py-3'>
              <div className='flex items-center gap-3'>
                <Label
                  htmlFor='wd-po'
                  className='flex-1 text-sm font-medium leading-[1.4] text-[#171a20]'
                >
                  {t('workingDays.rules.preventOverlaps')}
                </Label>
                <Switch
                  id='wd-po'
                  checked={state.prevent_overlaps}
                  onCheckedChange={value => set({ prevent_overlaps: value })}
                  className={switchClass}
                />
              </div>
            </div>

            <div className='border-b border-[#e7e9ef] py-3'>
              <div className='flex items-center gap-3'>
                <Label
                  htmlFor='wd-lpd'
                  className='flex-1 text-sm font-medium leading-[1.4] text-[#171a20]'
                >
                  {t('workingDays.rules.limitPerDay')}
                </Label>
                <Switch
                  id='wd-lpd'
                  checked={state.limit_per_day}
                  onCheckedChange={value => {
                    set({
                      limit_per_day: value,
                      max_jobs_per_day: value ? state.max_jobs_per_day : '',
                    })
                    setErrors(current => ({ ...current, max_jobs_per_day: '' }))
                  }}
                  className={switchClass}
                />
              </div>

              {state.limit_per_day && (
                <div className='mt-4 max-w-[554px]'>
                  <FieldLabel htmlFor='wd-mjpd' required>
                    {t('workingDays.fields.maxJobsPerDay')}
                  </FieldLabel>
                  <Input
                    id='wd-mjpd'
                    type='number'
                    min='1'
                    value={state.max_jobs_per_day}
                    onChange={event => {
                      set({ max_jobs_per_day: event.target.value })
                      setErrors(current => ({
                        ...current,
                        max_jobs_per_day: '',
                      }))
                    }}
                    className={cn(
                      fieldInputClass,
                      'mt-1',
                      errors.max_jobs_per_day &&
                        'border border-destructive bg-white'
                    )}
                  />
                  {errors.max_jobs_per_day && (
                    <p className='mt-1 text-sm text-destructive'>
                      {errors.max_jobs_per_day}
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className='py-3'>
              <div className='max-w-[554px]'>
                <FieldLabel htmlFor='wd-tt'>
                  {t('workingDays.fields.travelTime')}
                </FieldLabel>
                <Input
                  id='wd-tt'
                  type='number'
                  min='0'
                  value={state.travel_time}
                  placeholder='0'
                  onChange={event => set({ travel_time: event.target.value })}
                  className={cn(fieldInputClass, 'mt-1')}
                />
              </div>
            </div>
          </div>
        </SectionCard>

        <SectionCard title={t('workingDays.reminders.title')}>
          <div className='flex flex-col gap-1 max-w-[554px]'>
            <FieldLabel htmlFor='wd-rbi'>
              {t('workingDays.reminders.beforeIntervention')}
            </FieldLabel>
            <Input
              id='wd-rbi'
              type='number'
              value={state.reminder_before}
              placeholder='30'
              onChange={e => set({ reminder_before: e.target.value })}
              className={fieldInputClass}
            />
          </div>

          <div className='border-b border-[#e7e9ef] pb-3'>
            <div className='flex items-center gap-3'>
              <Label
                htmlFor='wd-nom'
                className='flex-1 text-sm font-medium leading-[1.4] text-[#171a20]'
              >
                {t('workingDays.reminders.notifyOnModification')}
              </Label>
              <Switch
                id='wd-nom'
                checked={state.notify_on_modification}
                onCheckedChange={v => set({ notify_on_modification: v })}
                className={switchClass}
              />
            </div>
          </div>

          <div className='max-w-[554px]'>
            <div className='flex flex-col gap-1'>
              <FieldLabel htmlFor='wd-trt'>
                {t('workingDays.reminders.defaultTaskTime')}
              </FieldLabel>
              <Input
                id='wd-trt'
                type='time'
                value={state.task_reminder_time}
                onChange={e => set({ task_reminder_time: e.target.value })}
                className={fieldInputClass}
              />
            </div>
          </div>
        </SectionCard>
      </div>

      <footer className='sticky bottom-0 z-20 mt-auto -mx-6 -mb-6 flex h-[72px] items-center justify-end border-t border-[#e7e9ef] bg-white px-6 shadow-[0_-1px_4px_0_rgba(38,45,66,0.05)]'>
        <div className='flex justify-end'>
          <Button
            className={saveButtonClass}
            onClick={save}
            disabled={isSaving}
          >
            {t('workingDays.saveChanges')}
          </Button>
        </div>
      </footer>
    </div>
  )
}

export default WorkingDaysHoursPanel
